import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector, Dispatch } from '@algoan/dashboard/store/store';
import { CustomerTable } from '../modules/customer-table';
import {
  linkCreationEnabledSelector,
  teamConfigSelector,
  widgetConfigSelector,
  accessTokenOrganizationIdSelector,
  accessTokenPayloadSelector,
  projectNameSelector,
  projectIdSelector,
} from '@algoan/dashboard/store/selectors';
import { Button } from '../shared-components/atomic-components';
import { Tooltip, TooltipPosition } from '@algoan/deep-dive/tooltip';
import { useIntl } from 'react-intl';
import { logAmplitudeEvent, AmplitudeEvent } from '@algoan/dashboard/analytics';
import { LinkDrawer } from '../modules/application/link-drawer';
import { indexPageCancellationToken, getConfigData, filterCustomers } from '@algoan/dashboard/store/thunks';
import { resetState as resetCustomerState, resetPreviousCustomerId } from '@algoan/dashboard/store/slice';
import { LinkIcon } from '../shared-components/icons';
import { batch } from 'react-redux';
import { monitoring, Tabs, Flag, useFeatureFlag } from '@algoan/dashboard/utils';
import { invitationMessages } from '../widgets/invitations/invitation-messages';
import { newFolderMessages } from '../widgets/new-folder/new-folder-messages';
import { useRouter } from 'next/router';
import { Layout, LayoutTheme, Whiteboard, LayoutTitle } from '../modules/layout';
import { messages } from '../modules/application/application-messages';
import Head from 'next/head';

const Index: React.FC = () => {
  const dispatch: Dispatch = useDispatch();
  const accessTokenPayload = useSelector(accessTokenPayloadSelector);
  const teamConfig = useSelector(teamConfigSelector);
  const projectId = useSelector(projectIdSelector);
  const projectName = useSelector((state) => (projectId ? projectNameSelector(state, projectId) : undefined));
  const organizationId = useSelector(accessTokenOrganizationIdSelector);
  const linkCreationEnabled = useSelector(linkCreationEnabledSelector);
  const widgetConfig = useSelector(widgetConfigSelector);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const intl = useIntl();
  const router = useRouter();
  const { active: pdfDemo } = useFeatureFlag(Flag.PDF_DEMO);

  const invitationEnabled = widgetConfig?.invitation;

  const handleCloseDrawer = useCallback((): void => {
    setDrawerOpen(false);
  }, []);

  useEffect(() => {
    if (projectId === undefined || organizationId === undefined) return;

    // reset application state
    batch(() => {
      dispatch(resetCustomerState());
      dispatch(resetPreviousCustomerId());
    });

    dispatch(getConfigData(organizationId, projectId)).then(() => {
      const invalidTeamState = accessTokenPayload?.team && (teamConfig === undefined || teamConfig.teams.length === 0);
      if (invalidTeamState) logInvalidTeamStateError();

      dispatch(
        filterCustomers({
          project: projectName ? { id: projectId, name: projectName } : undefined,
        }),
      );
    });
    return () => {
      indexPageCancellationToken?.cancel('Index page change');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, projectId, organizationId]);

  return (
    <Layout selectedTab={Tabs.HOME} shouldAddDropdown theme={LayoutTheme.ICE_BLUE}>
      <Head>
        <title key="title">{intl.formatMessage(messages.title)} - Dashboard</title>
      </Head>
      <LinkDrawer open={drawerOpen} onClose={handleCloseDrawer} />
      <LayoutTitle
        title={intl.formatMessage(messages.title)}
        actions={
          <S.ButtonContainer>
            {pdfDemo && (
              <S.Button
                data-testid="invitation-with-pdf-button"
                variant="secondary"
                onClick={() => {
                  router.push('/new-folder');
                }}
                icon={LinkIcon}
              >
                {intl.formatMessage(newFolderMessages.newFolderButton)}
              </S.Button>
            )}
            {linkCreationEnabled && !invitationEnabled && (
              <S.Button
                data-testid="create-link-button"
                variant="secondary"
                onClick={() => {
                  setDrawerOpen(true);
                  logAmplitudeEvent(AmplitudeEvent.LINK_START);
                }}
                icon={LinkIcon}
              >
                {intl.formatMessage(messages.createLink)}
              </S.Button>
            )}
            {invitationEnabled && (
              <S.Button
                data-testid="invitation-button"
                variant="secondary"
                onClick={() => {
                  router.push('/invitation');
                }}
                icon={LinkIcon}
              >
                {intl.formatMessage(invitationMessages.inviteButton)}
              </S.Button>
            )}
          </S.ButtonContainer>
        }
      />
      <Whiteboard>
        <CustomerTable />
      </Whiteboard>
      <Tooltip id="multiprojects_icon_tooltip" position={TooltipPosition.CURSOR} />
    </Layout>
  );
};

const logInvalidTeamStateError = (): void => {
  monitoring.logError({
    fingerprint: 'invalid_team_state',
    message: 'User does not belong to an organisation with teams, but has a team on their JWT token',
    context: { level: 'error' },
  });
};

const S = {
  Button: styled(Button)`
    border-color: ${(props) => props.theme.secondary.softBlue2};
    color: ${(props) => props.theme.secondary.softBlue2};
  `,
  ButtonContainer: styled.div`
    display: flex;
    gap: 24px;
  `,
};

export default Index;
